import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageContext";
import { slugFor } from "@/navConfig";
import { usePageMeta } from "@/lib/pageMeta";
import { FormDialogsProvider } from "@/components/FormDialogs";
import { Header } from "@/components/Header";
import { EVENT_ENDED } from "@/config";
import { Hero } from "@/components/sections/Hero";
import { SpeakersStrip } from "@/components/sections/SpeakersStrip";
import { CaseStudiesPromo } from "@/components/sections/CaseStudiesPromo";
import { WhoYouMeet } from "@/components/sections/WhoYouMeet";
import { Audience } from "@/components/sections/Audience";
import { IntroBlock } from "@/components/sections/IntroBlock";
import { WhatIsSummit } from "@/components/sections/WhatIsSummit";
import { Takeaways } from "@/components/sections/Takeaways";
import { Themes } from "@/components/sections/Themes";
import { About } from "@/components/sections/About";
import { Program } from "@/components/sections/Program";
import { Tickets } from "@/components/sections/Tickets";
import { Recap } from "@/components/sections/Recap";
import { Partners } from "@/components/sections/Partners";
import { Credibility } from "@/components/sections/Credibility";
import { FAQ } from "@/components/sections/FAQ";
import { FinalCTA } from "@/components/sections/FinalCTA";
import { Footer } from "@/components/sections/Footer";
import { Media } from "@/components/sections/Media";
import { Privacy } from "@/components/sections/Privacy";
import { CookieConsent } from "@/components/CookieConsent";
import { BackToTop } from "@/components/BackToTop";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const SITE_BG = "/img/site-bg.jpg";

function Layout() {
  return (
    <div className="min-h-screen bg-[#01154F] text-white antialiased selection:bg-[#CEA26B]">
      <div
        aria-hidden="true"
        data-testid="site-side-graphic"
        className="hidden lg:block fixed top-0 right-0 h-full w-[24vw] max-w-[440px] z-[1] pointer-events-none"
        style={{
          backgroundImage: `url(${SITE_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "right center",
          opacity: 0.08,
          WebkitMaskImage:
            "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 45%, transparent 100%)",
          maskImage:
            "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 45%, transparent 100%)",
        }}
      />
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <BackToTop />
      <CookieConsent />
    </div>
  );
}

const HOME_META = {
  cs: {
    title: "OBAL ROKU SUMMIT 2026 — Obal jako strategické rozhodnutí",
    description:
      "Odborný summit pro český a slovenský obalový trh. Strategie, regulace, inovace, průmysl a byznys v jednom dni.",
  },
  sk: {
    title: "OBAL ROKU SUMMIT 2026 — Obal ako strategické rozhodnutie",
    description:
      "Odborný summit pre český a slovenský obalový trh. Stratégie, regulácie, inovácie, priemysel a biznis v jednom dni.",
  },
  en: {
    title: "OBAL ROKU SUMMIT 2026 — Packaging as a Strategic Decision",
    description:
      "The expert summit for the Czech and Slovak packaging market. Strategy, regulation, innovation, industry and business in one day.",
  },
};

const HomeMeta = () => {
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  const m = HOME_META[lang] || HOME_META.cs;
  usePageMeta({ title: m.title, description: m.description, path: pathname });
  return null;
};

const Home = () => (
  <>
    <HomeMeta />
    <Hero />
    <IntroBlock />
    <WhatIsSummit />
    <Takeaways />
    <WhoYouMeet />
    <SpeakersStrip />
    <CaseStudiesPromo />
    <Credibility />
    <Audience />
    <FinalCTA />
  </>
);

const SubPage = ({ children }) => (
  <div className="pt-20">
    {children}
    <FinalCTA />
  </div>
);

function LangApp({ lang }) {
  const { lang: current, setLang } = useLanguage();
  useEffect(() => {
    if (current !== lang) setLang(lang);
  }, [lang, current, setLang]);
  const s = (key) => slugFor(key, lang);
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path={s("about")} element={<SubPage><About /></SubPage>} />
        <Route path={s("why")} element={<SubPage><About /></SubPage>} />
        <Route path={s("program")} element={<SubPage><Program /></SubPage>} />
        <Route path={s("themes")} element={<SubPage><Themes /></SubPage>} />
        <Route path={s("gala")} element={<SubPage><Program /></SubPage>} />
        <Route path={s("caseStudies")} element={<SubPage><Program /></SubPage>} />
        {!EVENT_ENDED && (
          <Route path={s("tickets")} element={<SubPage><Tickets /></SubPage>} />
        )}
        <Route path={s("recap")} element={<SubPage><Recap /></SubPage>} />
        <Route path={s("partners")} element={<SubPage><Partners /></SubPage>} />
        <Route path={s("faq")} element={<SubPage><FAQ /></SubPage>} />
        <Route path={s("media")} element={<SubPage><Media /></SubPage>} />
        <Route path={s("privacy")} element={<div className="pt-20"><Privacy /></div>} />
        <Route path="*" element={<Home />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <FormDialogsProvider>
          <ScrollToTop />
          <Routes>
            <Route path="/en/*" element={<LangApp lang="en" />} />
            <Route path="/sk/*" element={<LangApp lang="sk" />} />
            <Route path="/*" element={<LangApp lang="cs" />} />
          </Routes>
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#0A2060",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#F8FAFC",
              },
            }}
          />
        </FormDialogsProvider>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
